/**
 * Permission Management Hook
 * 
 * Provides utility functions to check user permissions
 * Compatible with RTK Query and Redux Toolkit auth state
 */

import { useAppSelector } from '@app/store';

/**
 * Hook for checking user permissions
 * 
 * @returns Object with permission checking functions
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
 *   
 *   if (hasPermission('DRIVER_CREATE')) {
 *     return <CreateDriverButton />;
 *   }
 *   
 *   if (hasAnyPermission(['DRIVER_VIEW', 'VEHICLE_VIEW'])) {
 *     return <DashboardView />;
 *   }
 *   
 *   if (hasAllPermissions(['DRIVER_CREATE', 'DRIVER_APPROVE'])) {
 *     return <AdvancedDriverForm />;
 *   }
 * }
 * ```
 */
export const usePermissions = () => {
  const { user } = useAppSelector(state => state.auth);
  
  /**
   * Check if user has a specific permission
   * 
   * @param permission - Permission string to check
   * @returns true if user has the permission, false otherwise
   * 
   * @example
   * ```tsx
   * const canCreate = hasPermission('DRIVER_CREATE');
   * const canApprove = hasPermission('DRIVER_APPROVE');
   * ```
   */
  const hasPermission = (permission: string): boolean => {
    if (!user?.permissions) return false;
    return user.permissions[permission] === true;
  };
  
  /**
   * Check if user has ANY of the specified permissions (OR logic)
   * 
   * @param permissions - Array of permission strings
   * @returns true if user has at least one permission, false otherwise
   * 
   * @example
   * ```tsx
   * // User needs DRIVER_VIEW OR VEHICLE_VIEW
   * const canViewDashboard = hasAnyPermission(['DRIVER_VIEW', 'VEHICLE_VIEW']);
   * ```
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user?.permissions) return false;
    return permissions.some(p => hasPermission(p));
  };
  
  /**
   * Check if user has ALL of the specified permissions (AND logic)
   * 
   * @param permissions - Array of permission strings
   * @returns true if user has all permissions, false otherwise
   * 
   * @example
   * ```tsx
   * // User needs both DRIVER_CREATE AND DRIVER_APPROVE
   * const canFullyManageDrivers = hasAllPermissions(['DRIVER_CREATE', 'DRIVER_APPROVE']);
   * ```
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user?.permissions) return false;
    return permissions.every(p => hasPermission(p));
  };
  
  return { hasPermission, hasAnyPermission, hasAllPermissions };
};

/**
 * Type-safe permission strings
 * Import from @services/rolesApi for full type safety
 */
export type PermissionString = string;
